import { useQuery } from '@apollo/client';
import { Toolbar, ToolbarButton, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { INavLinkGroup } from '@m365-admin/nav';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { GET_COUNTRY_CONFIGS } from '../../utils/statApi/CountryApi';
import CanAccess from '../common/canAccess/CanAccess';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import FullWidthHeader from '../common/headers/FullWidthHeader';
import MainDetailsView from '../common/mainDetail/MainDetailsView';
import { ICountryIdParams } from '../common/ParamTypes';
import CountryConfigEditPanel from './CountryConfigEditPanel';
import { ICountryConfiguration } from './CountryConfiguration.config';
import CountryConfigurationInformation from './CountryConfigurationInformation';

const CountryConfigurationView: React.FunctionComponent = (): JSX.Element => {
  const history = useHistory();
  const { countryId } = useParams<ICountryIdParams>();
  const [addMode, setIsAddMode] = useState(false);
  const [countryConfigData, setCountryConfigData] = useState<ICountryConfiguration>(null);
  const [isCountryConfigPanelOpen, setIsCountryConfigPanelOpen] = useState(false);
  const { data, loading, error } = useQuery(GET_COUNTRY_CONFIGS);
  const countryConfigurations: ICountryConfiguration[] = data?.countryConfigurations;

  useEffect(() => {
    if (!countryId && countryConfigurations && countryConfigurations.length > 0) {
      history.replace(`/countries/${countryConfigurations[0].id}`);
    }
  }, [countryConfigurations]);

  const links = countryConfigurations?.map((config) => {
    return {
      key: config.id.toString(),
      name: config.country.shortName,
      isSelected: countryId === config.id.toString(),
      onClick: (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
        ev.preventDefault();
        ev.stopPropagation();
        history.push(`/countries/${config.id}`);
      },
      url: config.id.toString(),
    };
  });

  const navLinks: INavLinkGroup[] = [
    {
      key: 'countryConfigurations',
      links: links || [],
    },
  ];

  const toggleAddCountryConfigPanel = () => {
    setIsAddMode(!addMode);
    setIsCountryConfigPanelOpen(!isCountryConfigPanelOpen);
  };

  const toggleCountryConfigPanel = () => {
    setIsAddMode(false);
    setIsCountryConfigPanelOpen(!isCountryConfigPanelOpen);
  };

  const renderHeader = (): JSX.Element => <h1>Country Configuration</h1>;

  return (
    <>
      <FullWidthHeader title={renderHeader} />
      <LoadingErrorMessage
        loading={loading}
        error={error}
        actionName="loading the Country Configuration List"
      />
      <MainDetailsView
        navLinkGroups={navLinks}
        navAriaLabel="Country configuration navigation"
        navSectionHeader={
          <CanAccess requestedAction="CountryConfiguration:AddEdit">
            <FluentProvider theme={webLightTheme}>
              <Toolbar aria-label="Use left and right arrow keys to navigate between commands">
                <ToolbarButton
                  icon={<AddRegular />}
                  onClick={toggleAddCountryConfigPanel}
                  appearance="subtle"
                >
                  New Country Configuration
                </ToolbarButton>
              </Toolbar>
            </FluentProvider>
          </CanAccess>
        }
      >
        {countryId && countryConfigurations?.length > 0 ? (
          <CountryConfigurationInformation
            toggleCountryConfigPanel={toggleCountryConfigPanel}
            setCountryConfigData={setCountryConfigData}
          />
        ) : null}
      </MainDetailsView>
      {isCountryConfigPanelOpen && (
        <CountryConfigEditPanel
          countryData={addMode ? ({} as ICountryConfiguration) : countryConfigData}
          closePanel={toggleCountryConfigPanel}
        />
      )}
    </>
  );
};

export default CountryConfigurationView;
